import { Caption, Icon, IconTile, SubsectionTitle, type IconTileColor } from '@if/mui'
import { Box, Card, CardActionArea, CardContent, Stack, styled } from '@mui/material'
import { ArrowRight, type LucideIcon } from 'lucide-react'
import type { MouseEventHandler, ReactNode } from 'react'

const Root = styled(Card)({ height: '100%' })

/**
 * The home grid gives every card the same fixed height, so long text has to be
 * capped rather than left to clip (`Card` hides its overflow): the FR strings run
 * well past their EN counterparts and wrap in the narrow columns.
 */
const CLAMP_LINES = 2
const clampSx = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: CLAMP_LINES,
  overflow: 'hidden',
  width: '100%',
} as const

const actionSx = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  // ButtonBase defaults to `align-items: center`; with the column direction that
  // would center the content horizontally — force stretch so it stays left-aligned.
  alignItems: 'stretch',
  '&:hover .nav-card-arrow, &.Mui-focusVisible .nav-card-arrow': { opacity: 1 },
} as const

export interface NavigationCardProps {
  /** Route the whole card links to. Mutually exclusive with `onClick`. */
  to?: string
  /** Click handler — renders the card as a button. Mutually exclusive with `to`. */
  onClick?: MouseEventHandler<HTMLElement>
  /** Lucide glyph — pass the component (`Calculator`), not an element. */
  icon: LucideIcon
  /** Palette tint for the icon tile. Defaults to `secondary` (the splash "baby
   *  blue" accent), matching the mockup's nav-card icons. */
  iconColor?: IconTileColor
  title: string
  /** Muted line under the title. A node so callers can compose (e.g. a count). */
  description?: ReactNode
  /** Trailing arrow beside the title. Defaults to `true`. */
  showArrow?: boolean
}

/**
 * A clickable dashboard navigation tile: a tinted icon tile top-left with an
 * optional arrow top-right, then the title and a muted description beneath.
 * Uses `CardContent` for the design-system's uniform card padding; the icon tile
 * and roled typography carry all the styling, so the card adds none of its own.
 */
export const NavigationCard = ({
  to,
  onClick,
  icon,
  iconColor = 'secondary',
  title,
  description,
  showArrow = true,
}: NavigationCardProps) => {
  const content = (
    <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <Stack spacing={1.5} alignItems="flex-start">
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ width: '100%' }}>
          <IconTile icon={icon} color={iconColor} />
          {showArrow && (
            <Box
              className="nav-card-arrow"
              sx={{ opacity: 0, transition: (theme) => theme.transitions.create('opacity') }}
            >
              <Icon icon={ArrowRight} size="sm" color="action" />
            </Box>
          )}
        </Stack>
        <SubsectionTitle sx={clampSx}>{title}</SubsectionTitle>
        {description !== undefined && (
          <Box sx={clampSx}>{typeof description === 'string' ? <Caption>{description}</Caption> : description}</Box>
        )}
      </Stack>
    </CardContent>
  )

  // The card is a link or a button, never both: MUI resolves its overloads on the props
  // actually present, so the branch is on the JSX rather than on a union-typed `href`.
  return (
    <Root>
      {to !== undefined ? (
        <CardActionArea href={to} sx={actionSx}>
          {content}
        </CardActionArea>
      ) : (
        <CardActionArea onClick={onClick} sx={actionSx}>
          {content}
        </CardActionArea>
      )}
    </Root>
  )
}
